import { trackConfig, type IFramework } from '@/framework';
import { type TextSelectionRange } from '@/utils/text-kit';
import {
    LayerType,
    TextAlignType,
    TextWritingType,
    type ILayerProps,
    type ITextLayerProps,
    type Optional,
} from '@/version-types';
import {
    applySnapshotSize,
    applyTextRangeAttrs,
    pushHistory,
    requestTextSVGPreview,
    unifyTextContentsAttrs,
    updateLayer,
    validateInputLayer,
} from '../utils';
import { getTextLayerHost } from '../utils/host';
import { type IShapeText } from './shape-text';

type Options = {
    range?: Optional<TextSelectionRange>;
    writeHistory?: boolean;
};

type Result = Promise<{ layer: ILayerProps; range: TextSelectionRange | null } | void>;

export interface IFontSize {
    /**
     * 设置字体大小
     * @param layerID - 图层 ID
     * @param fontSize - 字体大小
     * @param options - 配置项
     * @returns
     */
    setFontSize(layerID: string, fontSize: number, options?: Options): Result;
}

const FUNC_ID = trackConfig[LayerType.TEXT_LAYER].size;

export class FontSize implements IFontSize {
    private prevLayer: ILayerProps | null = null;

    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    /**
     * 更改字号后，新图层根据原始图层的位置及其文字对齐方式调整位置
     *
     * 水平方向：
     * - 左对齐 / 两端对齐：保持左上顶点不动，宽度向右侧扩展
     * - 居中对齐：图层中心点与原始图层中心点对齐，宽度向两侧扩展
     * - 右对齐：保持右上顶点不动，宽度向左侧扩展
     *
     * 垂直方向：
     * - 多行：保持顶部不动，高度向下扩展
     * - 单行：保持垂直中心不动
     *
     * 竖排文字始终保持垂直中心，水平方向沿用 textAlign 对应的锚点。
     *
     * @param prevLayer - 原始图层
     * @param nextLayer - 更新后的图层
     * @param options - 配置项
     *  - options.isMultipleLines - 是否多行
     * @returns
     */
    private refreshPosition(
        prevLayer: ILayerProps,
        nextLayer: ILayerProps,
        options: { isMultipleLines: boolean },
    ): ILayerProps {
        const { width: layerWidth, height: layerHeight } = prevLayer;
        const { textAlign, writingMode } = nextLayer.data as ITextLayerProps;

        const { isMultipleLines } = options;

        let nextLayerTop = nextLayer.top + (layerHeight - nextLayer.height) / 2;
        if (writingMode === TextWritingType.HORIZONTAL_TB && isMultipleLines) nextLayerTop = nextLayer.top;

        let nextLayerLeft = nextLayer.left;
        if (textAlign === TextAlignType.CENTER) {
            nextLayerLeft = nextLayerLeft + (layerWidth - nextLayer.width) / 2;
        } else if (textAlign === TextAlignType.RIGHT) {
            nextLayerLeft = nextLayerLeft + (layerWidth - nextLayer.width);
        }

        return {
            ...nextLayer,
            top: nextLayerTop,
            left: nextLayerLeft,
        };
    }

    /**
     * 使用真实 Text Layer 宿主为字号候选重新排版。
     *
     * @param prevLayer - 修改前的 Text Layer。
     * @param fontSize - 即将写入的字号。
     * @param range - 可选的编辑选区。
     * @returns 可提交的图层、选区与 snapshot；宿主尚未就绪时返回 null。
     */
    private prepare(prevLayer: ILayerProps, fontSize: number, range: TextSelectionRange | null) {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        let nextLayerData: ITextLayerProps = { ...prevLayerData };
        let nextRange: TextSelectionRange | null = range;

        if (range && range.start !== range.end) {
            const result = applyTextRangeAttrs(nextLayerData, range, {
                key: 'fontSize',
                value: fontSize,
            });
            nextLayerData = { ...nextLayerData, textContents: result.textContents };
            nextRange = result.range;
        } else {
            nextLayerData = { ...nextLayerData, fontSize };

            const { textContents: prevTextContents = [] } = prevLayerData;
            if (prevTextContents.length > 0) {
                const nextTextContents = unifyTextContentsAttrs(prevTextContents, { fontSize });
                nextLayerData = { ...nextLayerData, textContents: nextTextContents };
            }
        }

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { shapeExtension } = prevLayerData;
        if (shapeExtension) return { layer: nextLayer, range: nextRange, snapshot: null };

        const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id)?.layout(nextLayer);
        // 非 ready 状态不能提交本次字号对应的几何。
        if (resolution?.status !== 'ready') return null;
        const snapshot = resolution.value;

        nextLayer = applySnapshotSize(nextLayer, snapshot);
        nextLayer = this.refreshPosition(prevLayer, nextLayer, { isMultipleLines: snapshot.lineMetrics.length > 1 });

        return {
            layer: nextLayer,
            range: nextRange,
            snapshot,
        };
    }

    private setGroup(prevLayer: ILayerProps, fontSize: number, options: Exclude<Options, 'range'>) {
        const { writeHistory = false } = options;

        const children = prevLayer.children;
        if (children.length === 0) return;

        const layers: ILayerProps[] = [];
        for (const child of children) {
            const result = this.prepare(child, fontSize, null);
            if (!result) return { layer: prevLayer, range: null };

            layers.push(result.layer);
        }

        const nextLayer = { ...prevLayer, children: layers };

        requestTextSVGPreview(this.framework, writeHistory ? 'commit' : 'update', nextLayer);
        updateLayer(this.framework, nextLayer, { activate: true, dataOnly: !writeHistory });
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, FUNC_ID);
            this.prevLayer = null;
        }

        return { layer: nextLayer, range: null };
    }

    private async setSingle(prevLayer: ILayerProps, fontSize: number, options: Options) {
        const { range = null, writeHistory = false } = options;

        const result = this.prepare(prevLayer, fontSize, range);
        if (!result) return { layer: prevLayer, range };

        let nextLayer = result.layer;

        const { shapeExtension } = prevLayer.data as ITextLayerProps;
        if (shapeExtension && writeHistory) {
            const layer = await this.shapeText.refreshShapeTextRender(nextLayer);
            if (layer) nextLayer = layer;
        }

        if (writeHistory) requestTextSVGPreview(this.framework, 'commit', nextLayer);
        else if (result.snapshot) requestTextSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, {
            activate: true,
            dataOnly: Boolean(result.snapshot) && !writeHistory,
        });
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer, FUNC_ID);
            this.prevLayer = null;
        }

        return {
            layer: nextLayer,
            range: result.range,
        };
    }

    async setFontSize(layerID: string, fontSize: number, options: Options) {
        const prevLayer = (() => {
            if (this.prevLayer && this.prevLayer.id === layerID) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!validateInputLayer(prevLayer)) return;

        const { writeHistory = false } = options;
        const result =
            prevLayer.layerType === LayerType.GROUP_LAYER
                ? this.setGroup(prevLayer, fontSize, { writeHistory })
                : await this.setSingle(prevLayer, fontSize, options);

        return result;
    }
}
